import dayjs, { Dayjs } from 'dayjs'
import { DateTimeFormats } from 'helpers/datetime'
import { useSearchParams } from 'hooks/useSearchParams'
import {
  getDefaultInvitationDateRange,
  getInvitationStatusFilterSelection,
  toInvitationStatusFilterParam,
} from '../helpers'
import { useCallback, useMemo } from 'react'
import { InvitationStatusFilterValue } from '../constants'
import { InvitationListFilters } from '../types'

type UseInvitationFiltersResult = {
  dateRangeValue: [Dayjs, Dayjs]
  selectedStatus: InvitationStatusFilterValue
  handleDateRangeChange: (dates: [Dayjs | null, Dayjs | null] | null) => void
  handleStatusChange: (value: InvitationStatusFilterValue) => void
}

export function useInvitationFilters(): UseInvitationFiltersResult {
  const { params, setParams } = useSearchParams<InvitationListFilters>()

  const handleDateRangeChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null) => {
      if (dates?.[0] && dates[1]) {
        setParams(
          {
            benefitEffectiveStartDate: dates[0].format(DateTimeFormats.a),
            benefitEffectiveEndDate: dates[1].format(DateTimeFormats.a),
            page: '1',
          },
          { replace: true }
        )
        return
      }
      if (dates === null) {
        setParams(
          { ...getDefaultInvitationDateRange(), page: '1' },
          {
            replace: true,
          }
        )
        return
      }

      setParams(
        {
          benefitEffectiveStartDate: undefined,
          benefitEffectiveEndDate: undefined,
        },
        { replace: true }
      )
    },
    [setParams]
  )

  const dateRangeValue = useMemo((): [Dayjs, Dayjs] => {
    if (params.benefitEffectiveStartDate && params.benefitEffectiveEndDate) {
      return [
        dayjs(params.benefitEffectiveStartDate),
        dayjs(params.benefitEffectiveEndDate),
      ]
    }
    const defaults = getDefaultInvitationDateRange()
    return [
      dayjs(defaults.benefitEffectiveStartDate),
      dayjs(defaults.benefitEffectiveEndDate),
    ]
  }, [params.benefitEffectiveStartDate, params.benefitEffectiveEndDate])

  const selectedStatus = useMemo((): InvitationStatusFilterValue => {
    return getInvitationStatusFilterSelection(params.status)
  }, [params.status])

  const handleStatusChange = useCallback(
    (value: InvitationStatusFilterValue) => {
      setParams(
        { status: toInvitationStatusFilterParam(value), page: '1' },
        { replace: true }
      )
    },
    [setParams]
  )

  return {
    dateRangeValue,
    selectedStatus,
    handleDateRangeChange,
    handleStatusChange,
  }
}
